import { Suspense } from "react";
import { Navigate, Outlet, type RouteObject } from "react-router-dom";
import { Permissions } from "@/auth/permissions";
import { RequirePermission } from "@/auth/require-permission";
import { LoadingScreen } from "@/components/loading-screen";
import { useI18n } from "@/locales/i18n-provider";
import { NotificationCenterLayout } from "@/surfaces/standalone/layouts/notifications/layout";
import { SystemConfigurationLayout } from "@/surfaces/standalone/layouts/system-config/layout";
import { WorkspaceLayout } from "@/surfaces/standalone/layouts/workspace/layout";
import { SETTINGS_PERMISSIONS } from "@/surfaces/standalone/navigation/navigation-model";
import { LegacyTicketDetailRedirect } from "./legacy-ticket-detail-redirect";
import { standalonePage } from "./page-loader";
import { SettingsIndexRedirect } from "./settings-index-redirect";
import { WorkspaceIndexRedirect } from "./workspace-index-redirect";

function RouteLoadingFallback() {
  const { t } = useI18n();
  return <LoadingScreen variant="page" title={t("route.loading")} />;
}

export const standaloneRoutes: RouteObject[] = [
  {
    path: "/",
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: "/",
        element: <WorkspaceLayout />,
        children: [
          { index: true, element: <WorkspaceIndexRedirect /> },
          { path: "search", element: standalonePage("search") },
          {
            path: "tickets",
            element: <RequirePermission permissions={[Permissions.tickets.view]} />,
            children: [
              { index: true, element: <Navigate replace to="all" /> },
              { path: "all", element: standalonePage("tickets/all") },
              { path: "assigned", element: standalonePage("tickets/assigned") },
              { path: "unassigned", element: standalonePage("tickets/unassigned") },
              { path: "participating", element: standalonePage("tickets/participating") },
              { path: "uncategorized", element: standalonePage("tickets/uncategorized") },
              { path: "recently-resolved", element: standalonePage("tickets/recently-resolved") },
              {
                path: "create",
                element: <RequirePermission permissions={[Permissions.tickets.create]}>{standalonePage("tickets/create")}</RequirePermission>,
              },

              { path: "all/:ticketId", element: standalonePage("tickets/detail", { listView: "all" }) },
              { path: "assigned/:ticketId", element: standalonePage("tickets/detail", { listView: "assigned" }) },
              { path: "unassigned/:ticketId", element: standalonePage("tickets/detail", { listView: "unassigned" }) },
              { path: "participating/:ticketId", element: standalonePage("tickets/detail", { listView: "participating" }) },
              { path: "uncategorized/:ticketId", element: standalonePage("tickets/detail", { listView: "uncategorized" }) },
              { path: "recently-resolved/:ticketId", element: standalonePage("tickets/detail", { listView: "recently-resolved" }) },
              { path: ":ticketId", element: <LegacyTicketDetailRedirect /> },
            ],
          },
          {
            path: "notifications",
            element: <NotificationCenterLayout />,
            children: [
              { index: true, element: <Navigate replace to="all" /> },
              { path: "all", element: standalonePage("notifications/all") },
              { path: "preferences", element: standalonePage("notifications/preferences") },
            ],
          },
          {
            path: "overview",
            element: <RequirePermission permissions={[Permissions.reports.view]} />,
            children: [{ index: true, element: standalonePage("overview") }],
          },
          {
            path: "reports",
            element: <RequirePermission permissions={[Permissions.reports.view]} />,
            children: [{ index: true, element: standalonePage("reports") }],
          },
          {
            path: "customers",
            element: <RequirePermission permissions={[Permissions.customers.view]} />,
            children: [
              { index: true, element: standalonePage("customers") },
              { path: ":customerId", element: standalonePage("customers/detail") },
            ],
          },
        ],
      },
      {
        path: "/settings",
        element: <SystemConfigurationLayout />,
        children: [
          {
            element: <RequirePermission permissions={SETTINGS_PERMISSIONS} />,
            children: [
              { index: true, element: <SettingsIndexRedirect /> },
              { path: "projects", element: standalonePage("settings/projects") },
              { path: "ticket-categories", element: standalonePage("settings/categories") },
              { path: "routing-rules", element: standalonePage("settings/routing-rules") },
              { path: "organization", element: standalonePage("settings/organization") },
              { path: "teams", element: <Navigate replace to="/settings/organization?tab=teams" /> },
              { path: "agents", element: <Navigate replace to="/settings/organization?tab=agents" /> },
              { path: "ai", element: standalonePage("settings/ai") },
              // 兜底：还没接后端的分区走占位页（静态路径优先于 `:section`，已落地的分区不会被它抢走）。
              { path: ":section", element: standalonePage("settings/placeholder") },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate replace to="/" /> },
];
